import React, { FC, ReactNode, useState } from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import some from 'lodash/some';
import styled, { css } from 'styled-components';

import BookMagnifyingGlassIcon from '@ad-scripts/presentation/components/lib/icons/book_magnifying_glass_icon';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import NODE_TYPES from 'src/models/Ivrs/NodeTypes';
import { FILTER_NODES_OPTIONS } from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import NODES_MAPPER from 'src/presentation/components/pages/ivr/constants/nodesMapper';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import { IconContainer } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import { Row } from 'src/presentation/components/forms/Layout';
import { START_NODE_ID } from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';
import { getNodeName } from 'src/presentation/components/pages/ivr/components/helpers/nodeHelpers';
import { isTemporarySplitNode } from 'src/presentation/components/pages/ivr/helpers/ivrHelpers';

type TypeNodeListProps = {
  onListItemClick: Function;
  nodes?: object;
  searchText?: string;
  ivrErrors: TypeErrorNode[] | { [key: string]: TypeErrorNode };
};

type TypeErrorNode = {
  attr: string;
  nodeId: string;
  code: string;
  detail: string;
  meta: object;
};

type TypeNodeListFilterProps = {
  aidNodeListPrefix: string;
  errNodeListState: [boolean, Function];
};

type TypeNodeListContentProps = {
  onListItemClick: Function;
  listNodes: any;
  ivrErrors: any;
};

const NodeList: FC<TypeNodeListProps> = ({ nodes, searchText = '', onListItemClick, ivrErrors }) => {
  const ivrErrorsObject = mapIvrErrorsArrayToObject(ivrErrors as TypeErrorNode[]);

  const [isErrNodeList, setErrNodeList] = useState<boolean>(false);
  const aidNodeListPrefix: string = 'node_list';
  let listNodes: any = {
    ...nodes,
    ...{
      [START_NODE_ID]: {
        type: NODE_TYPES.START_NODE,
        ivrNode: { id: START_NODE_ID, name: NODES_MAPPER[NODE_TYPES.START_NODE].label },
      },
    },
  };

  const getAidNodeList = (): string => {
    if (searchText) {
      return `${aidNodeListPrefix}_by_search`;
    }

    if (isErrNodeList) {
      return `${aidNodeListPrefix}_errors`;
    }

    return `${aidNodeListPrefix}_all_nodes`;
  };

  if (isErrNodeList) {
    listNodes = filter(listNodes, node => has(ivrErrorsObject, node.ivrNode.id));
  }

  if (searchText) {
    listNodes = getFilteredNodesBySearch(listNodes, searchText);
  }

  return (
    <ListWrapper data-aid={getAidNodeList()} role="list">
      <NodeListFilter aidNodeListPrefix={aidNodeListPrefix} errNodeListState={[isErrNodeList, setErrNodeList]} />
      <NodeListContent listNodes={listNodes} onListItemClick={onListItemClick} ivrErrors={ivrErrorsObject} />
    </ListWrapper>
  );
};

const NodeListFilter: FC<TypeNodeListFilterProps> = ({ aidNodeListPrefix, errNodeListState }) => {
  const [isErrNodeList, setErrNodeList] = errNodeListState;
  const { allNodes, errors } = FILTER_NODES_OPTIONS;
  const selectedValue: string = isErrNodeList ? errors.value : allNodes.value;

  const handleChangeDropdown = (value: string) => {
    if (value === allNodes.value) {
      return setErrNodeList(false);
    }
    setErrNodeList(true);
  };

  return (
    <StyledRow>
      <StyledH3>Nodes</StyledH3>
      <StyledDropdown
        data-aid={`${aidNodeListPrefix}_filter_dropdown`}
        options={[allNodes, errors]}
        value={selectedValue}
        onChange={handleChangeDropdown}
      />
    </StyledRow>
  );
};

const getFilteredNodesBySearch = (nodes, searchText) =>
  filter(nodes, node => {
    const ivrNodeId = get(node, 'ivrNode.id', '');
    const isTemporaryNode = isTemporarySplitNode(ivrNodeId);
    const nodeTypeLabel = isTemporaryNode
      ? NODES_MAPPER[NODE_TYPES.IVR_NODE_SCHEDULE_TEMPORARY_SPLIT].label
      : get(NODES_MAPPER, `${get(node, 'type')}.label`, '');
    const nodeDescription = get(node, 'ivrNode.description', '');
    const nodeName =
      node.type === NODE_TYPES.IVR_NODE_DIAL
        ? get(node, 'ivrNode.selection', get(node, 'ivrNode.description', ''))
        : get(node, 'ivrNode.name', '');
    const filterCriteria = [nodeTypeLabel, nodeDescription, nodeName];
    return some(filterCriteria, criterion => criterion.toLowerCase().indexOf(searchText.toLowerCase()) > -1);
  });

const NodeListContent: FC<TypeNodeListContentProps> = ({ listNodes, onListItemClick, ivrErrors }): any => {
  if (isEmpty(listNodes)) {
    return (
      <NoResults data-aid="noResultsSearch">
        <BookMagnifyingGlassIcon />
        <h3>No Results</h3>
      </NoResults>
    );
  }

  function renderNodeListItemWithTooltip(icon: ReactNode, label: ReactNode, onClick: Function, nodeId: string) {
    const getTooltipMessage = () => {
      const messages = ivrErrors[nodeId];
      const messagesLength = messages.length;
      const visibleQuantity = 2;
      const hasMultipleMessages = ivrErrors[nodeId].length > 1;

      const renderRestQuantityInfo = () => {
        const restQuantity = messagesLength - visibleQuantity;
        const textContent = `+${restQuantity} more error${restQuantity > 1 ? 's' : ''}`;
        return <StyledQuantityInfoItem key={restQuantity}>{textContent}</StyledQuantityInfoItem>;
      };

      return (
        <StyledMessageList data-aid={`node_${nodeId}-tooltip-message`}>
          {messages.slice(0, visibleQuantity).map((ivrErr, idx) => (
            <StyledMessageItem key={idx} hasMultipleMessages={hasMultipleMessages}>
              {ivrErr.detail}
            </StyledMessageItem>
          ))}
          {messagesLength > visibleQuantity && renderRestQuantityInfo()}
        </StyledMessageList>
      );
    };
    return (
      <StyledTooltip key={nodeId} message={getTooltipMessage()}>
        {renderNodeListItem(icon, label, onClick, nodeId)}
      </StyledTooltip>
    );
  }

  function renderNodeListItem(icon: ReactNode, label: ReactNode, onClick: Function, nodeId: string) {
    return (
      <ListItem className="nodeListItem" key={nodeId} data-aid={`node_${nodeId}`} onClick={onClick} role="listitem">
        {icon}
        {label}
      </ListItem>
    );
  }

  return map(listNodes, node => {
    const label = getNodeName(node);
    const { type, ivrNode: nodeConfig } = node;
    const id = get(nodeConfig, 'id', '');

    const onClick = () => onListItemClick(id);

    const IconComponent = isTemporarySplitNode(id)
      ? NODES_MAPPER[NODE_TYPES.IVR_NODE_SCHEDULE_TEMPORARY_SPLIT].icon
      : get(NODES_MAPPER, `${type}.icon`);

    const isErrorNode = ivrErrors[nodeConfig.id];
    const icon = (
      <IconContainer isErrorNode={isErrorNode}>
        {/* @ts-ignore */}
        <IconComponent />
      </IconContainer>
    );

    return isErrorNode
      ? renderNodeListItemWithTooltip(icon, label, onClick, nodeConfig.id)
      : renderNodeListItem(icon, label, onClick, nodeConfig.id);
  });
};

function mapIvrErrorsArrayToObject(ivrErrors: TypeErrorNode[]) {
  return ivrErrors.reduce((acc, ivrErr: TypeErrorNode) => {
    const { nodeId } = ivrErr;

    if (nodeId) {
      acc[nodeId] = acc[nodeId] ?? [];
      acc[nodeId].push(ivrErr);
    }

    return acc;
  }, {});
}

const NoResults = styled.div`
  padding-top: 100px;
  text-align: center;
  .bookMagnifyingGlassIcon {
    height: 66px;
    width: 69px;
  }
  .bookMagnifyingGlassIcon-circle {
    fill: ${p => p.theme.colors.gray100};
  }
  .bookMagnifyingGlassIcon-stroke {
    stroke: ${p => p.theme.colors.green400};
  }
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 14px;
`;

const StyledDropdown = styled(Dropdown)`
  flex-grow: 0;
  min-width: 112px;
  button {
    border: 0;
    &:hover {
      background: 0;
    }
    &:focus {
      border: 0;
      color: ${p => p.theme.colors.black};
    }

    div {
      text-align: center;
    }
  }
`;

const StyledRow = styled(Row)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
`;

const StyledH3 = styled(H3)`
  margin-right: 10px;
  margin-bottom: 0;
`;

const ListItem = styled(Row)`
  display: flex;
  align-items: center;
  color: ${p => p.theme.colors.black};
  margin: 0;
  margin-right: auto;
  padding-bottom: 6px;
  ${IconContainer} {
    fill: ${p => p.theme.colors.gray500};
  }

  &:hover {
    cursor: pointer;
    color: ${p => p.theme.colors.green500};
    ${IconContainer} {
      fill: ${p => p.theme.colors.green500};
    }
  }
`;

const StyledTooltip = styled(Tooltip)`
  margin-right: auto;

  &:hover svg {
    fill: ${p => p.theme.colors.green500};
  }
`;

const StyledMessageList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: center;
  list-style: none;
  margin 0;
  padding: 0;
`;

const bulletPointCss = css`
  padding-left: 10px;
  &::before {
    content: '';
    display: inline-block;
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    width: 5px;
    height: 5px;
    background: ${({ theme }) => theme.colors.white};
    border-radius: 50%;
  }
`;

const StyledMessageItem = styled.li`
  position: relative;
  padding: 4px 0px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 309px;
  ${({ hasMultipleMessages }) => (hasMultipleMessages ? bulletPointCss : '')};
`;

const StyledQuantityInfoItem = styled.li`
  text-align: center;
  opacity: 0.7;

  &::before {
    display: none;
  }
`;

export default NodeList;
